/* ── numbers — every figure can open its own time series ────── */
import { useDispatch } from 'react-redux';
import Icon3D from '../Icons3D';
import { openTimeSeries } from '../../state/uiSlice';
import { useMetricEntity } from './metrics';

export function Num({ value, label, metricKey, entity, holding, scenario, type = 'cur', tone, className = '' }) {
  const dispatch = useDispatch();
  const scoped = useMetricEntity();
  const subject = entity ?? scoped;
  const cls = ['num', tone, metricKey ? 'tsnum' : '', className].filter(Boolean).join(' ');
  if (!metricKey) return <span className={cls}>{value}</span>;

  /**
   * Opening the series consumes the click.
   *
   * These figures sit inside rows that are themselves links — a fund row opens
   * its deep-dive, a bar row opens a screen — so without this the click both
   * opens the modal and navigates away underneath it.
   *
   * `scenario` travels with the figure because a risk metric has one series per
   * run: the number in a stress row and the number in the base row above it are
   * the same metric on different simulations.
   */
  const open = (e) => {
    e.stopPropagation();
    dispatch(openTimeSeries({ label, raw: String(value), metricKey, entity: subject, holding, scenario, type }));
  };

  return (
    <span
      className={cls}
      role="button"
      tabIndex={0}
      title={`${label} — view history`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(e);
        }
      }}
    >
      {value}
    </span>
  );
}

/**
 * A numeric table cell. The design makes every `td.num` carrying a digit a
 * time-series link, so this is the cell to use for figures in tables.
 *
 * Omitting `metricKey` falls back to a `count.*` key, whose modal explains that
 * the figure is a point-in-time count. Passing `null` declines the link
 * outright, for a figure that is neither a count nor a series — an attribution
 * effect is computed for one window against one benchmark, so there is nothing
 * to chart and nothing a count modal would truthfully say about it.
 */
export function NumCell({ value, label, metricKey, entity, holding, scenario, type, tone, bold, children }) {
  const key = metricKey === null ? null : metricKey ?? countMetric(label);
  const num = (
    <Num
      value={value}
      label={label}
      metricKey={key}
      entity={entity}
      holding={holding}
      scenario={scenario}
      type={type}
    />
  );
  return (
    <td className={`num ${tone || ''}`.trim()}>
      {bold ? <b>{num}</b> : num}
      {children}
    </td>
  );
}

/**
 * Fallback metric key for a figure nobody gave one to.
 *
 * The design makes every KPI, stat and bar-row value clickable. Where the
 * backend publishes a series the caller names it; where it does not — a
 * point-in-time count like "9 clients off target" — this keeps the affordance
 * and the modal explains that no history is stored for it.
 */
export const countMetric = (label) =>
  `count.${String(label || 'metric').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;

/** Speak-a-stat button — present on every card header and KPI in the design. */
export function Say({ label, onSpeak }) {
  return (
    <button className="say" title={`Explain ${label}`} onClick={() => onSpeak?.(label)}>
      <Icon3D name="spark" size={14} />
    </button>
  );
}
